/**
 * Stop Status Display Utilities
 *
 * Determines display text for stop availability based on inventory status,
 * walk-up availability, overfilled state, and hurry-up deadlines.
 */

import type { Stop } from "@/types/location";

export interface StopStatusDisplay {
  /** Primary status text (e.g., "Preorders Closed", "Hurry!") */
  text: string;
  /** Additional message (e.g., "Walk-ups available while supplies last") */
  subtext?: string;
  /** CSS class for styling */
  className: string;
  /** Whether the stop is selectable/available for interaction */
  isAvailable: boolean;
  /** Whether add-to-cart should be disabled for all products */
  checkoutDisabled: boolean;
}

/**
 * Compute a cutoff date for a stop (stop date minus N days)
 */
function getCutoffDate(stop: Stop, beforeDays: number | string | undefined): Date | null {
  if (!beforeDays) return null;
  const days = typeof beforeDays === "string" ? parseInt(beforeDays, 10) : beforeDays;
  if (isNaN(days) || days <= 0) return null;
  const stopDate = new Date(stop.date);
  stopDate.setDate(stopDate.getDate() - days);
  stopDate.setHours(23, 59, 0, 0);
  return stopDate;
}

/**
 * Compute the sold-out cutoff date for a stop
 * (stop date minus sold_out_before_days)
 */
function getSoldOutDate(stop: Stop): Date | null {
  return getCutoffDate(stop, stop.sold_out_before_days);
}

/**
 * Compute the hurry-up cutoff date for a stop
 * (stop date minus hurry_up_before_days)
 */
function getHurryUpDate(stop: Stop): Date | null {
  return getCutoffDate(stop, stop.hurry_up_before_days);
}

/**
 * Format a date as MM/DD/YYYY for display
 */
function formatDateShort(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Get display status for a stop based on inventory and walk-up availability
 *
 * Priority order (matching legacy getCheckoutStatusAndText):
 * 1. isWalkUpOnly (no preorder ever) → "Walk-ups available..." + checkoutDisabled
 * 2. (sold_out || overfilled) + isWalkUpOnly → same message, still disabled
 * 3. (sold_out || overfilled) + walk_up → "Preorders Closed" + walkup subtext
 * 4. (sold_out || overfilled) + no walk_up → "Preorders Closed"
 * 5. hurry_up → "Hurry! Order by midnight on [date]"
 * 6. default → "Available"
 */
export function getStopStatusDisplay(
  stop: Stop,
  options?: { isWalkUpOnly?: boolean },
): StopStatusDisplay {
  const now = new Date();
  const isOverfilled = stop.overfilled || false;
  const hasWalkUp = stop.walk_up || false;
  const isWalkUpOnly = options?.isWalkUpOnly ?? false;

  // Client-side sold_out: server flag OR past the sold_out_before_days cutoff
  const soldOutDate = getSoldOutDate(stop);
  const isSoldOut = stop.inventory_status?.sold_out
    || (soldOutDate !== null && now >= soldOutDate);

  // Client-side hurry_up: server flag OR past the hurry_up_before_days cutoff (but not yet sold out)
  const hurryUpDate = getHurryUpDate(stop);
  const isHurryUp = stop.inventory_status?.hurry_up
    || (hurryUpDate !== null && now >= hurryUpDate);

  // Walk-up ONLY locations: no preorder available, checkout always disabled
  // Shows same message whether sold out or not (preorder never existed)
  if (isWalkUpOnly) {
    return {
      text: "Walk-ups available while supplies last",
      className: "bg-warning-soft text-warning-text",
      isAvailable: true,
      checkoutDisabled: true,
    };
  }

  // Preorders closed with walk-up available (mixed preorder + walk-up)
  if ((isSoldOut || isOverfilled) && hasWalkUp) {
    return {
      text: "Preorders Closed",
      subtext: "Walk-ups available while supplies last",
      className: "bg-warning-soft text-warning-text",
      isAvailable: true,
      checkoutDisabled: true,
    };
  }

  // Preorders closed without walk-up
  if (isSoldOut || isOverfilled) {
    return {
      text: "Preorders Closed",
      className: "bg-error-soft text-error-text",
      isAvailable: false,
      checkoutDisabled: true,
    };
  }

  // Hurry up — deadline approaching (preorder still available, may have walk-up too)
  if (isHurryUp) {
    const deadlineDate = getSoldOutDate(stop) ?? getHurryUpDate(stop);
    const deadlineText = deadlineDate
      ? `Hurry! Order by midnight on ${formatDateShort(deadlineDate)}`
      : "Limited Availability";

    return {
      text: deadlineText,
      subtext: hasWalkUp
        ? "Walk-ups available while supplies last"
        : undefined,
      className: "bg-warning-soft text-warning-text",
      isAvailable: true,
      checkoutDisabled: false,
    };
  }

  // Available
  return {
    text: "Available",
    className: "bg-success-soft text-success-text",
    isAvailable: true,
    checkoutDisabled: false,
  };
}
